import { REQUEST_ERROR_CODE } from '@/const/code';
import { AppError, ErrorType } from '@/helpers/error';
import { retryAsync } from '@/utils';
import { shouldRetryFetchSSEError } from './sseHelper';

/**
 * fetchSSE 的选项配置
 */
export interface FetchSSEOptions extends RequestInit {
  /** 最大重试次数，默认 3 次 */
  maxRetries?: number;
  /** 重试延迟（毫秒），默认 600ms */
  retryDelay?: number;
  /** 重试因子，默认 1.5 */
  retryFactor?: number;
}

/** SSE 请求封装 */
export async function fetchSSE(
  url: string,
  options?: FetchSSEOptions,
): Promise<Response> {
  const { headers, maxRetries = 3, retryDelay = 600, retryFactor = 1.5, ...fetchOptions } = options || {};

  // 默认配置：POST、JSON、SSE、credentials
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'text/event-stream',
  };

  // 合并用户提供的 headers
  const mergedHeaders = { ...defaultHeaders, ...headers };

  const finalFetchOptions: RequestInit = {
    method: 'POST',
    credentials: 'include',
    ...fetchOptions,
    headers: mergedHeaders,
  };

  // fetch 请求，网络错误快速重试
  return await retryAsync(
    async () => {
      const res = await fetch(url, finalFetchOptions);

      if (!res.ok) {
        const message = res.statusText || `HTTP ${res.status}`;
        const json = await res.json();
        if (json.code === REQUEST_ERROR_CODE.CREDIT_ERROR) {
          throw new AppError(
            ErrorType.CREDIT_ERROR,
            message,
            res.status,
          );
        }

        throw new AppError(
          ErrorType.HTTP_ERROR,
          message,
          res.status,
        );
      }

      return res;
    },
    {
      retries: maxRetries,
      delay: retryDelay,
      factor: retryFactor,
      shouldRetry: shouldRetryFetchSSEError,
    },
  );
}
